import type { SceneDocument } from '@/lib/scene-document';

import { flushSync } from 'react-dom';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useCanvasSnapshot } from '@/lib/canvas-snapshot';
import { normalizeSceneDocument } from '@/lib/scene-document';
import { useOrganization } from '@/context/organization-context';
import { useV1ProductControllerGetByVariantIds } from '@/apis/backend/generated/products/products';
import React, { useRef, useMemo, Fragment, useState, useEffect, useContext, useCallback, useLayoutEffect } from 'react';
import { SOCIAL_ICONS, socialIconUrl, BLANK_PRODUCT_ID, resolveMergeTagsInTree } from '@nexmail/shared-util/email-builder';
import {
  useEditorStore,
  CanvasKitEditor,
  useHasEditorStore,
  CanvasKitEditorProvider,
  type CanvasKitEditorHandle,
} from '@/components/canvas-editor/CanvasKitEditor';

import { Box, Chip, Link, Stack, Paper, Button, Popper, Divider, TextField, Typography, CircularProgress } from '@mui/material';
import {
  Add as AddIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  DragIndicator as DragIndicatorIcon,
  StrikethroughS as StrikethroughIcon,
  FormatColorText as FormatColorTextIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  FormatUnderlined as FormatUnderlinedIcon,
} from '@mui/icons-material';

import { useDocStore } from './state';
import { isDescendantOrSelf } from './utils';
import { useDynamicPicker } from './dynamic-picker';
import { EmailCanvasViewport, type ViewportPersistedState } from './EmailCanvasViewport';

import type {
  Border,
  TextNode,
  EmailNode,
  GroupNode,
  ImageNode,
  ButtonNode,
  ColumnNode,
  NavbarNode,
  SocialNode,
  SpacerNode,
  BorderStyle,
  DividerNode,
  SectionNode,
  WrapperNode,
  CanvasObject,
  EmailDocument,
  ProductFeedNode,
  CanvasSectionNode,
} from './types';

const INDIGO = '#6366F1';
const INDIGO_SOFT = 'rgba(99,102,241,0.08)';
const TEXT_DIM = '#9CA3AF';
const DROP_DASH = 'rgba(99,102,241,0.35)';

const CHECKER_BG = {
  backgroundColor: '#D4D4D8',
  backgroundImage:
    'linear-gradient(45deg, #B4B4B8 25%, transparent 25%), linear-gradient(-45deg, #B4B4B8 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #B4B4B8 75%), linear-gradient(-45deg, transparent 75%, #B4B4B8 75%)',
  backgroundSize: '20px 20px',
  backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
};

// Stacks overlay + gradient + image into a CSS background shorthand. Mirrors
// the compile-time rasterization so the preview matches the sent email even
// though composition happens in CSS here and in PNG at send time.
function toSectionBackgroundCss(style: SectionNode['style']): React.CSSProperties {
  if (!style) return {};
  const layers: string[] = [];
  if (style.backgroundOverlay) {
    const { color, opacity, gradient } = style.backgroundOverlay;
    if (gradient) {
      const stops = gradient.stops
        .map((s: { color: string; position: number }) => `${overlayRgba(s.color, opacity)} ${Math.round(s.position)}%`)
        .join(', ');
      layers.push(`linear-gradient(${Math.round(gradient.angle)}deg, ${stops})`);
    } else {
      const rgba = overlayRgba(color, opacity);
      layers.push(`linear-gradient(0deg, ${rgba}, ${rgba})`);
    }
  }
  if (style.backgroundGradient) {
    const stops = style.backgroundGradient.stops
      .map((s) => `${s.color} ${Math.round(s.position)}%`)
      .join(', ');
    layers.push(`linear-gradient(${Math.round(style.backgroundGradient.angle)}deg, ${stops})`);
  }
  if (style.backgroundImage) {
    layers.push(`url(${style.backgroundImage})`);
  }
  return {
    backgroundColor:
      style.backgroundColor || style.backgroundGradient?.stops?.[0]?.color || undefined,
    backgroundImage: layers.length ? layers.join(', ') : undefined,
    backgroundSize: style.backgroundSize || (layers.length ? 'cover' : undefined),
    backgroundPosition: style.backgroundPosition || (layers.length ? 'center' : undefined),
    backgroundRepeat: layers.length ? 'no-repeat' : undefined,
  };
}

// After the global merge-tag resolve, walk the cloned doc and re-point each
// canvas-section's `canvasJson` back to the original (unresolved) reference.
// ActiveCanvasSection seeds the editor from canvasJson and writes edits back
// to the parent doc — if the seed has resolved tokens, those resolved values
// would persist on save and a brand swap would no longer re-skin the section.
// ReadOnlyCanvasSection clones+resolves on its own, so passing it the raw
// reference is also fine.
function restoreCanvasSectionRawJson(target: EmailDocument, source: EmailDocument): void {
  const rawById = new Map<string, CanvasSectionNode['props']['canvasJson']>();
  walkCanvasSections(source, (n) => {
    if (n.props.canvasJson) rawById.set(n.id, n.props.canvasJson);
  });
  walkCanvasSections(target, (n) => {
    const raw = rawById.get(n.id);
    if (raw) n.props.canvasJson = raw;
  });
}

function walkCanvasSections(doc: EmailDocument, visit: (node: CanvasSectionNode) => void): void {
  const stack: EmailNode[] = [doc.root];
  while (stack.length) {
    const cur = stack.pop()!;
    if (cur.type === 'canvas-section') visit(cur);
    if ('children' in cur && Array.isArray(cur.children)) {
      stack.push(...(cur.children as EmailNode[]));
    }
  }
}

function overlayRgba(color: string, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  const hex = color.trim().replace(/^#/, '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return color;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

interface EmailBuilderPreviewProps {
  document: EmailDocument;
  selectedNodeId: string | null;
  /** Pass null to clear the selection (e.g. background click). */
  onSelectNode: (nodeId: string | null) => void;
  /** Externally-driven hover highlight (e.g. from the left-rail tree). */
  hoveredNodeId?: string | null;
  /** Publishes the currently hovered preview node id so other panels (tree) can mirror it. */
  onHoverNode?: (nodeId: string | null) => void;
  onRequestAdd: (anchor: HTMLElement, parentId: string, index?: number) => void;
  viewportWidth?: number;
  activeCanvasSectionId?: string | null;
  canvasEditorRef?: React.RefObject<CanvasKitEditorHandle | null>;
  onCanvasDocumentChange?: () => void;
  /** @deprecated No longer used — pan state replaces scroll position. Kept for API compat. */
  scrollPositionRef?: React.MutableRefObject<number>;
  brandPrimaryColor?: string;
  brandLogoUrl?: string;
  brandName?: string;
  /** Per-platform icon URL override from the brand profile. Looked up before the CDN default. */
  brandSocialIcons?: Record<string, string | undefined>;
  /** Restored viewport state from a previous mount (survives canvas-mode switch). */
  initialViewportState?: ViewportPersistedState;
  /** Called when zoom/pan changes so the parent can persist across remounts. */
  onViewportStateChange?: (state: ViewportPersistedState) => void;
  /** Disables viewport panning while a canvas section is being edited. */
  disablePan?: boolean;
  /** Inline text editing: called when user edits a text node inline. Pass format when the edit introduces or removes HTML. */
  onUpdateNodeContent?: (nodeId: string, content: string, format?: 'plain' | 'html') => void;
  /** Section toolbar: called for move/duplicate/delete actions on top-level sections. */
  onSectionAction?: (nodeId: string, action: SectionAction) => void;
  /** Fired when the user starts dragging a section by its toolbar grip handle. Caller tracks the dragged node id (e.g. in a ref) for use on drop. */
  onSectionDragStart?: (nodeId: string) => void;
  /** Fired when a drag ends over a top-level section drop zone. Caller resolves target index and invokes moveNode. */
  onSectionDrop?: (targetNode: EmailNode, position: 'before' | 'after') => void;
  /** Image replace: called when user double-clicks an image node. */
  onPickImage?: (nodeId: string) => void;
  /** Column resize: called when user drags the border between two adjacent columns. */
  onResizeColumns?: (colAId: string, colAWidth: string, colBId: string, colBWidth: string) => void;
}

export function EmailBuilderPreview({
  document,
  selectedNodeId,
  onSelectNode,
  hoveredNodeId,
  onHoverNode,
  onRequestAdd,
  viewportWidth,
  activeCanvasSectionId,
  canvasEditorRef,
  onCanvasDocumentChange,
  brandPrimaryColor,
  brandLogoUrl,
  brandName,
  brandSocialIcons,
  initialViewportState,
  onViewportStateChange,
  disablePan = false,
  onUpdateNodeContent,
  onSectionAction,
  onSectionDragStart,
  onSectionDrop,
  onPickImage,
  onResizeColumns,
}: EmailBuilderPreviewProps) {
  const { t } = useTranslation('emailBuilder');
  const emailHeightRef = useRef<HTMLDivElement>(null);

  // Resolve $brand.* / $auto.* tags across the whole document for rendering.
  // Without this, raw tokens (e.g. "$brand.borderRadius") sit inside plain
  // node.style.borderRadius and flow into CSS as invalid values, so buttons
  // render with 0px corners. Canvas-section nodes already do their own
  // resolution downstream and ActiveCanvasSection seeds the editor from the
  // raw canvasJson — to preserve that contract we swap each canvas-section's
  // canvasJson back to the original (unresolved) reference after the walk.
  const picker = useDynamicPicker();
  const brand = picker?.brand ?? null;
  const renderDocument = useMemo(() => {
    const cloned = structuredClone(document);
    if (brand) {
      resolveMergeTagsInTree(cloned, brand, undefined);
      restoreCanvasSectionRawJson(cloned, document);
    }
    return cloned;
  }, [document, brand]);

  const bodyChildren = useMemo(() => renderDocument.root.children.slice(1), [renderDocument]);
  const empty = bodyChildren.length === 0;
  const emailWidth = viewportWidth ?? renderDocument.root.props.width;

  return (
    <EmailCanvasViewport
      emailWidth={emailWidth}
      emailHeight={emailHeightRef.current?.scrollHeight ?? 900}
      initialState={initialViewportState}
      onStateChange={onViewportStateChange}
      disablePan={disablePan}
      onBackgroundClick={() => onSelectNode(null)}
    >
      {/* email-section marker on the column so pointer-down doesn't trigger pan */}
      <Box data-email-section ref={emailHeightRef} sx={{ position: 'relative', width: emailWidth }}>
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              right: -12,
              zIndex: 2,
              display: 'flex',
              gap: 0.5,
            }}
          >
            <Chip
              label={`${emailWidth}px`}
              size="small"
              sx={{
                height: 22,
                fontSize: 10,
                fontWeight: 600,
                color: '#374151',
                bgcolor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Box>

          <Box
            // Clicking the email's own background (not a section — those stop
            // propagation in useSelectableProps) clears the current selection.
            onClick={(e) => {
              if (e.target === e.currentTarget) onSelectNode(null);
            }}
            sx={{
              width: emailWidth,
              minHeight: 480,
              bgcolor: document.root.style?.backgroundColor || '#FFFFFF',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.05)',
              borderRadius: 0.5,
              overflow: 'visible',
            }}
          >
            {empty ? (
              <EmptyState
                rootId={document.root.id}
                onRequestAdd={onRequestAdd}
                onSelect={onSelectNode}
              />
            ) : (
              <Box>
                {bodyChildren.map((child, i) => (
                  <Fragment key={child.id}>
                    <SectionDropZone node={child} onDrop={onSectionDrop}>
                      <PreviewNode
                        node={child}
                        selectedNodeId={selectedNodeId}
                        onSelectNode={onSelectNode}
                        hoveredNodeId={hoveredNodeId}
                        onHoverNode={onHoverNode}
                        onRequestAdd={onRequestAdd}
                        isMobile={viewportWidth !== undefined}
                        activeCanvasSectionId={activeCanvasSectionId}
                        canvasEditorRef={canvasEditorRef}
                        onCanvasDocumentChange={onCanvasDocumentChange}
                        brandPrimaryColor={brandPrimaryColor}
                        brandLogoUrl={brandLogoUrl}
                        brandName={brandName}
                        brandSocialIcons={brandSocialIcons}
                        onUpdateNodeContent={onUpdateNodeContent}
                        onSectionAction={onSectionAction}
                        onSectionDragStart={onSectionDragStart}
                        onSectionDrop={onSectionDrop}
                        onPickImage={onPickImage}
                        onResizeColumns={onResizeColumns}
                      />
                    </SectionDropZone>
                    {/* head occupies root.children[0]; body children start at index 1 */}
                    <InsertZone
                      variant="section"
                      label={t('emailBuilderPreview.insertZone.addSection', 'Add section')}
                      onClick={(e) => onRequestAdd(e.currentTarget, renderDocument.root.id, i + 2)}
                    />
                  </Fragment>
                ))}
              </Box>
            )}
          </Box>
      </Box>
    </EmailCanvasViewport>
  );
}


function InsertZone({
  onClick,
  label,
  variant = 'section',
}: {
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  label?: string;
  variant?: 'section' | 'inline';
}) {
  const { t } = useTranslation('emailBuilder');
  const resolvedLabel = label ?? t('emailBuilderPreview.insertZone.addSection', 'Add section');
  const [hovered, setHovered] = useState(false);
  const isInline = variant === 'inline';
  const collapsedHeight = isInline ? 4 : 6;
  const expandedHeight = isInline ? 18 : 28;
  const pillHeight = isInline ? 18 : 22;
  const pillFontSize = isInline ? 9 : 10;
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        height: hovered ? expandedHeight : collapsedHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        position: 'relative',
        zIndex: 1,
        transition: 'height 120ms ease',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: hovered ? 2 : 1,
          bgcolor: hovered ? INDIGO : 'transparent',
          transform: 'translateY(-50%)',
          transition: 'all 120ms ease',
        }}
      />
      {hovered && (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: INDIGO,
            color: '#FFFFFF',
            fontSize: pillFontSize,
            fontWeight: 700,
            px: isInline ? 1.25 : 1.5,
            height: pillHeight,
            borderRadius: 999,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            letterSpacing: '0.02em',
            boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
          }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: isInline ? 11 : 12 }} />
          {resolvedLabel}
        </Box>
      )}
    </Box>
  );
}

function EmptyState({
  rootId,
  onRequestAdd,
  onSelect,
}: {
  rootId: string;
  onRequestAdd: (anchor: HTMLElement, parentId: string, index?: number) => void;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation('emailBuilder');
  return (
    <Box
      onClick={() => onSelect(rootId)}
      sx={{
        flex: 1,
        minHeight: 480,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 5,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#EEF2FF',
          color: INDIGO,
        }}
      >
        <AddIcon sx={{ fontSize: 20 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827', mb: 0.5 }}>
          {t('emailBuilderPreview.empty.title', 'Start building')}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#6B7280', maxWidth: 320 }}>
          {t('emailBuilderPreview.empty.description', 'Add a wrapper, section, or canvas section to begin. Use the tree on the left or the button below.')}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="contained"
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        onClick={(e) => {
          e.stopPropagation();
          onRequestAdd(e.currentTarget, rootId);
        }}
        sx={{
          textTransform: 'none',
          fontSize: 12,
          fontWeight: 600,
          bgcolor: INDIGO,
          '&:hover': { bgcolor: '#4F46E5' },
          boxShadow: 'none',
        }}
      >
        {t('emailBuilderPreview.empty.addFirstSection', 'Add first section')}
      </Button>
    </Box>
  );
}

export type SectionAction = 'moveUp' | 'moveDown' | 'duplicate' | 'delete';

interface PreviewNodeBaseProps {
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  hoveredNodeId?: string | null;
  onHoverNode?: (nodeId: string | null) => void;
  onRequestAdd: (anchor: HTMLElement, parentId: string, index?: number) => void;
  isMobile?: boolean;
  activeCanvasSectionId?: string | null;
  canvasEditorRef?: React.RefObject<CanvasKitEditorHandle | null>;
  onCanvasDocumentChange?: () => void;
  brandPrimaryColor?: string;
  brandLogoUrl?: string;
  brandName?: string;
  brandSocialIcons?: Record<string, string | undefined>;
  /** Called when the user edits a text node inline (double-click in preview). Pass format when the edit introduces or removes HTML. */
  onUpdateNodeContent?: (nodeId: string, content: string, format?: 'plain' | 'html') => void;
  /** Called when user triggers an action from the section hover toolbar. */
  onSectionAction?: (nodeId: string, action: SectionAction) => void;
  /** Called when the user grabs a section's toolbar drag handle. */
  onSectionDragStart?: (nodeId: string) => void;
  /** Called when a drag is dropped on a top-level section. */
  onSectionDrop?: (targetNode: EmailNode, position: 'before' | 'after') => void;
  /** Called when user double-clicks an image node to replace its source. */
  onPickImage?: (nodeId: string) => void;
  /** Called when user drag-resizes a column border. */
  onResizeColumns?: (colAId: string, colAWidth: string, colBId: string, colBWidth: string) => void;
}

function useSelectableProps(
  node: { id: string },
  selectedNodeId: string | null,
  onSelectNode: (id: string) => void,
  hoveredNodeId?: string | null,
  onHoverNode?: (id: string | null) => void
) {
  const [localHovered, setLocalHovered] = useState(false);
  const selected = selectedNodeId === node.id;
  const hovered = localHovered || hoveredNodeId === node.id;
  return {
    selected,
    hovered,
    handlers: {
      onMouseEnter: (e: React.MouseEvent) => {
        e.stopPropagation();
        setLocalHovered(true);
        onHoverNode?.(node.id);
      },
      onMouseLeave: () => {
        setLocalHovered(false);
        onHoverNode?.(null);
      },
      onClick: (e: React.MouseEvent) => {
        // Bail when the click landed inside an active canvas surface — those
        // wrappers don't stop click propagation, and re-selecting this node
        // would deactivate the canvas. Plain text / inline markup inside
        // contentEditable still bubbles here normally and gets selected.
        if ((e.target as HTMLElement).closest('[data-canvas-active]')) return;
        e.stopPropagation();
        onSelectNode(node.id);
      },
    },
    outlineSx: selected
      ? { outline: `2px solid ${INDIGO}`, outlineOffset: -2 }
      : hovered
        ? { outline: `1px solid rgba(99,102,241,0.5)`, outlineOffset: -1 }
        : { outline: 'none' },
  };
}

export type { PreviewNodeBaseProps };
export function PreviewNode(props: { node: EmailNode } & PreviewNodeBaseProps) {
  const { node, ...rest } = props;
  switch (node.type) {
    case 'wrapper':
      return <PreviewWrapper node={node} {...rest} />;
    case 'section':
      return <PreviewSection node={node} {...rest} />;
    case 'group':
      return <PreviewGroup node={node} {...rest} />;
    case 'column':
      return <PreviewColumn node={node} {...rest} />;
    case 'text':
      return <PreviewText node={node} {...rest} />;
    case 'image':
      return <PreviewImage node={node} {...rest} />;
    case 'button':
      return <PreviewButton node={node} {...rest} />;
    case 'divider':
      return <PreviewDivider node={node} {...rest} />;
    case 'spacer':
      return <PreviewSpacer node={node} {...rest} />;
    case 'social':
      return <PreviewSocial node={node} {...rest} />;
    case 'navbar':
      return <PreviewNavbar node={node} {...rest} />;
    case 'product-feed':
      return <PreviewProductFeed node={node} {...rest} />;
    case 'canvas-section':
      return <PreviewCanvasSection node={node} {...rest} />;
    case 'canvas-shape':
    case 'canvas-text':
    case 'canvas-image':
    case 'canvas-path':
    case 'canvas-group':
      return <PreviewRootCanvasObject node={node as CanvasObject} {...rest} />;
    default:
      return null;
  }
}

// ─── Figma-style selection label ───────────────────────────────────────────

function SelectionLabel({ label }: { label: string }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: -20,
        left: 0,
        bgcolor: INDIGO,
        color: '#FFFFFF',
        fontSize: 9,
        fontWeight: 700,
        px: '6px',
        py: '2px',
        borderRadius: '3px 3px 0 0',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        pointerEvents: 'none',
        zIndex: 15,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

// ─── Section drop zone ─────────────────────────────────────────────────────
//
// Wraps each top-level section so a drag from another section's toolbar grip
// can land before or after it. The zone draws an INDIGO indicator line at
// the top or bottom edge based on cursor Y-position in the hit rect. Caller
// owns the actual reorder — this only reports target + position.

function SectionDropZone({
  node,
  onDrop,
  children,
}: {
  node: EmailNode;
  onDrop?: (target: EmailNode, position: 'before' | 'after') => void;
  children: React.ReactNode;
}) {
  const [zone, setZone] = useState<'before' | 'after' | null>(null);
  if (!onDrop) return <>{children}</>;
  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        setZone(y < rect.height / 2 ? 'before' : 'after');
      }}
      onDragLeave={() => setZone(null)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const z = zone;
        setZone(null);
        if (z) onDrop(node, z);
      }}
      sx={{
        position: 'relative',
        '&::before': zone === 'before'
          ? {
              content: '""',
              position: 'absolute',
              top: -2,
              left: 0,
              right: 0,
              height: 3,
              bgcolor: INDIGO,
              borderRadius: 1,
              pointerEvents: 'none',
              zIndex: 30,
            }
          : undefined,
        '&::after': zone === 'after'
          ? {
              content: '""',
              position: 'absolute',
              bottom: -2,
              left: 0,
              right: 0,
              height: 3,
              bgcolor: INDIGO,
              borderRadius: 1,
              pointerEvents: 'none',
              zIndex: 30,
            }
          : undefined,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Section hover toolbar ──────────────────────────────────────────────────

function SectionToolbar({
  nodeId,
  onAction,
  onDragStart,
}: {
  nodeId: string;
  onAction: (a: SectionAction) => void;
  onDragStart?: (nodeId: string) => void;
}) {
  const { t } = useTranslation('emailBuilder');
  const buttonSx = {
    background: '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    width: 32,
    height: 32,
    borderRadius: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#27272A',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.08)',
    transition: 'all 80ms ease',
    '&:hover': {
      bgcolor: '#F4F4F5',
      color: '#09090B',
    },
  } as const;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: '100%',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {onDragStart ? (
        <Box
          title={t('emailBuilderPreview.sectionToolbar.dragToReorder', 'Drag to reorder')}
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            // Dummy payload — some browsers won't start a drag without
            // dataTransfer data. Caller tracks the actual id out-of-band.
            e.dataTransfer.setData('text/plain', nodeId);
            e.dataTransfer.effectAllowed = 'move';
            onDragStart(nodeId);
          }}
          sx={{
            ...buttonSx,
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            '&:hover': {
              bgcolor: '#F4F4F5',
              color: '#09090B',
            },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
      ) : null}
      {([
        { action: 'moveUp' as SectionAction, icon: 'lucide:arrow-up', title: t('emailBuilderPreview.sectionToolbar.moveUp', 'Move up') },
        { action: 'moveDown' as SectionAction, icon: 'lucide:arrow-down', title: t('emailBuilderPreview.sectionToolbar.moveDown', 'Move down') },
        { action: 'duplicate' as SectionAction, icon: 'lucide:copy', title: t('emailBuilderPreview.sectionToolbar.duplicate', 'Duplicate') },
        { action: 'delete' as SectionAction, icon: 'lucide:trash-2', title: t('emailBuilderPreview.sectionToolbar.delete', 'Delete'), danger: true },
      ]).map(({ action, icon, title, danger }) => (
        <Box
          key={action}
          component="button"
          title={title}
          onClick={(e) => { e.stopPropagation(); onAction(action); }}
          sx={{
            ...buttonSx,
            color: danger ? '#DC2626' : '#27272A',
            '&:hover': {
              bgcolor: danger ? '#FEF2F2' : '#F4F4F5',
              color: danger ? '#B91C1C' : '#09090B',
            },
          }}
        >
          <Icon icon={icon} fontSize={16} />
        </Box>
      ))}
    </Box>
  );
}

// ─── Column resize handle ───────────────────────────────────────────────────

const SNAP_POINTS = [16.67, 25, 33.33, 40, 50, 60, 66.67, 75, 83.33];

function snapPct(v: number) {
  for (const s of SNAP_POINTS) {
    if (Math.abs(v - s) < 3) return s;
  }
  return Math.round(v * 100) / 100;
}

function ColResizeHandle({
  leftColId, leftWidth, rightColId, rightWidth, containerRef, onResize,
}: {
  leftColId: string; leftWidth: string;
  rightColId: string; rightWidth: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onResize: (aId: string, aW: string, bId: string, bW: string) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const stateRef = useRef({ startX: 0, startLeft: 50, startRight: 50, containerWidth: 0 });

  const parsePct = (w: string) => parseFloat(w) || 50;

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const cw = containerRef.current?.getBoundingClientRect().width ?? 600;
    stateRef.current = {
      startX: e.clientX,
      startLeft: parsePct(leftWidth),
      startRight: parsePct(rightWidth),
      containerWidth: cw,
    };
    setDragging(true);

    const onMove = (me: MouseEvent) => {
      // Live visual feedback is handled by CSS cursor only (no intermediate state)
      void me;
    };

    const onUp = (me: MouseEvent) => {
      const { startX, startLeft, startRight, containerWidth } = stateRef.current;
      const dxPct = ((me.clientX - startX) / containerWidth) * 100;
      const total = startLeft + startRight;
      const newLeft = Math.max(10, Math.min(total - 10, snapPct(startLeft + dxPct)));
      const newRight = Math.round((total - newLeft) * 100) / 100;
      setDragging(false);
      onResize(leftColId, `${newLeft}%`, rightColId, `${newRight}%`);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [leftColId, leftWidth, rightColId, rightWidth, containerRef, onResize]);

  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        width: 8,
        flexShrink: 0,
        cursor: 'col-resize',
        position: 'relative',
        zIndex: 5,
        mx: '-4px',
        '&:hover .resize-line': { opacity: 1, bgcolor: INDIGO },
        ...(dragging && { '& .resize-line': { opacity: 1, bgcolor: INDIGO } }),
      }}
    >
      <Box
        className="resize-line"
        sx={{
          position: 'absolute',
          top: '10%',
          bottom: '10%',
          left: '50%',
          width: 2,
          transform: 'translateX(-50%)',
          bgcolor: 'rgba(99,102,241,0.4)',
          borderRadius: 1,
          opacity: 0,
          transition: 'opacity 100ms ease, background-color 100ms ease',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

function PreviewWrapper({ node, ...rest }: { node: WrapperNode } & PreviewNodeBaseProps) {
  const { t } = useTranslation('emailBuilder');
  const { handlers, outlineSx, hovered, selected } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const containsSelected = !!rest.selectedNodeId && isDescendantOrSelf(node, rest.selectedNodeId);
  return (
    <Box
      {...handlers}
      sx={{
        position: 'relative',
        p: spacingToCss(node.style?.padding) || 0,
        bgcolor: node.style?.backgroundColor,
        ...outlineSx,
      }}
    >
      {selected && <SelectionLabel label={t('emailBuilderPreview.selectionLabel.wrapper', 'Wrapper')} />}
      {containsSelected && rest.onSectionAction && (
        <SectionToolbar
          nodeId={node.id}
          onAction={(a) => rest.onSectionAction!(node.id, a)}
          onDragStart={rest.onSectionDragStart}
        />
      )}
      {node.children.map((child) => (
        <PreviewNode key={child.id} node={child} {...rest} />
      ))}
    </Box>
  );
}

function PreviewSection({ node, ...rest }: { node: SectionNode } & PreviewNodeBaseProps) {
  const { t } = useTranslation('emailBuilder');
  const { handlers, selected, hovered } = useSelectableProps(
    node,
    rest.selectedNodeId,
    rest.onSelectNode,
    rest.hoveredNodeId,
    rest.onHoverNode
  );
  const containsSelected = !!rest.selectedNodeId && isDescendantOrSelf(node, rest.selectedNodeId);
  const children = node.children ?? [];
  const resolveColor = useResolveColor();
  const bg = toSectionBackgroundCss(node.style);
  const resolvedBg = resolveColor(bg.backgroundColor);
  return (
    <Box
      {...handlers}
      sx={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        boxSizing: 'border-box',
        p: spacingToCss(node.style?.padding) ?? '20px 24px',
        bgcolor: resolvedBg || 'transparent',
        backgroundImage: bg.backgroundImage,
        backgroundSize: bg.backgroundSize,
        backgroundPosition: bg.backgroundPosition,
        backgroundRepeat: bg.backgroundRepeat,
        borderRadius: cornerRadiusCss(node.style?.borderRadius),
        ...bordersToCss(node.style, resolveColor),
        outline: selected
          ? `2px solid ${INDIGO}`
          : hovered
            ? `1px solid rgba(99,102,241,0.5)`
            : 'none',
        outlineOffset: -1,
      }}
    >
      {selected && <SelectionLabel label={node.name || t('emailBuilderPreview.selectionLabel.section', 'Section')} />}
      {containsSelected && rest.onSectionAction && (
        <SectionToolbar
          nodeId={node.id}
          onAction={(a) => rest.onSectionAction!(node.id, a)}
          onDragStart={rest.onSectionDragStart}
        />
      )}
      {children.length === 0 ? (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            rest.onRequestAdd(e.currentTarget as HTMLElement, node.id);
          }}
          sx={{
            width: '100%',
            minHeight: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TEXT_DIM,
            fontSize: 11,
            fontWeight: 500,
            cursor: 'pointer',
            border: `1.5px dashed rgba(99,102,241,0.25)`,
            borderRadius: '6px',
            gap: 0.5,
            '&:hover': { color: INDIGO, borderColor: INDIGO, bgcolor: INDIGO_SOFT },
            transition: 'all 120ms ease',
          }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: 13 }} />
          {t('emailBuilderPreview.section.addColumn', 'Add column')}
        </Box>
      ) : (
        <SurroundingBackgroundContext.Provider value={resolvedBg || undefined}>
          {children.map((child) => <PreviewNode key={child.id} node={child} {...rest} />)}
        </SurroundingBackgroundContext.Provider>
      )}
    </Box>
  );
}

function PreviewGroup({ node, ...rest }: { node: GroupNode } & PreviewNodeBaseProps) {
  const { handlers, outlineSx } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const stacks = rest.isMobile && node.props.preventStackingOnMobile === false;
  const containerRef = useRef<HTMLDivElement>(null);
  const cols = node.children.filter(c => c.type === 'column') as ColumnNode[];
  const canResize = !stacks && cols.length >= 2 && !!rest.onResizeColumns;

  return (
    <Box
      ref={containerRef}
      {...handlers}
      sx={{
        display: 'flex',
        flexDirection: stacks ? 'column' : 'row',
        width: node.props.width || '100%',
        gap: 0,
        ...(stacks && { '& > *': { width: '100% !important', flex: '1 1 auto !important' } }),
        ...outlineSx,
      }}
    >
      {node.children.map((child, i) => {
        const next = node.children[i + 1];
        const showHandle = canResize && child.type === 'column' && next?.type === 'column';
        return (
          <Fragment key={child.id}>
            <PreviewNode node={child} {...rest} />
            {showHandle && (
              <ColResizeHandle
                leftColId={child.id}
                leftWidth={(child as ColumnNode).props.width || '50%'}
                rightColId={next.id}
                rightWidth={(next as ColumnNode).props.width || '50%'}
                containerRef={containerRef}
                onResize={rest.onResizeColumns!}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}

function PreviewColumn({ node, ...rest }: { node: ColumnNode } & PreviewNodeBaseProps) {
  const { t } = useTranslation('emailBuilder');
  const { handlers, selected, hovered } = useSelectableProps(
    node,
    rest.selectedNodeId,
    rest.onSelectNode,
    rest.hoveredNodeId,
    rest.onHoverNode
  );
  const children = node.children ?? [];
  const isEmpty = children.length === 0;
  const widthPct = node.props.width?.endsWith('%') ? node.props.width : undefined;
  const resolveColor = useResolveColor();
  const surroundingBg = useContext(SurroundingBackgroundContext);
  const ownBg = resolveColor(node.style?.backgroundColor);
  const childSurroundingBg = ownBg || surroundingBg;
  return (
    <Box
      {...handlers}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: widthPct ? `0 0 ${widthPct}` : '1 1 100%',
        width: widthPct ? widthPct : node.props.width || '100%',
        boxSizing: 'border-box',
        p: spacingToCss(node.style?.padding) || 0,
        bgcolor: ownBg || 'transparent',
        borderRadius: cornerRadiusCss(node.style?.borderRadius),
        ...bordersToCss(node.style, resolveColor),
        // A floor for the EMPTY state only: it keeps a column nobody has filled
        // yet big enough to click and drop into. Applying it to a column that
        // has content invents space the email doesn't have — a Figma import
        // slices into rows as short as 29px, and each one rendered here as a
        // 60px box, so the canvas showed gaps between slices that the compiled
        // email (mj-column, no min-height) never had.
        minHeight: isEmpty ? 60 : undefined,
        outline: selected
          ? `2px solid ${INDIGO}`
          : hovered
            ? `1px solid rgba(99,102,241,0.5)`
            : 'none',
        outlineOffset: -1,
        position: 'relative',
      }}
    >
      {selected && <SelectionLabel label={widthPct ? t('emailBuilderPreview.selectionLabel.columnWithWidth', 'Column {{width}}', { width: widthPct }) : t('emailBuilderPreview.selectionLabel.column', 'Column')} />}
      {isEmpty ? (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            rest.onRequestAdd(e.currentTarget as HTMLElement, node.id);
          }}
          sx={{
            flex: 1,
            minHeight: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TEXT_DIM,
            fontSize: 11,
            fontWeight: 500,
            cursor: 'pointer',
            border: `1.5px dashed rgba(99,102,241,0.25)`,
            borderRadius: '6px',
            m: '4px',
            gap: 0.5,
            '&:hover': { color: INDIGO, borderColor: INDIGO, bgcolor: INDIGO_SOFT },
            transition: 'all 120ms ease',
          }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: 13 }} />
          {t('emailBuilderPreview.column.addContent', 'Add content')}
        </Box>
      ) : (
        <SurroundingBackgroundContext.Provider value={childSurroundingBg || undefined}>
          <InsertZone
            variant="inline"
            label={t('emailBuilderPreview.column.add', 'Add')}
            onClick={(e) => rest.onRequestAdd(e.currentTarget, node.id, 0)}
          />
          {children.map((child, i) => (
            <Fragment key={child.id}>
              <PreviewNode node={child} {...rest} />
              <InsertZone
                variant="inline"
                label={t('emailBuilderPreview.column.add', 'Add')}
                onClick={(e) => rest.onRequestAdd(e.currentTarget, node.id, i + 1)}
              />
            </Fragment>
          ))}
        </SurroundingBackgroundContext.Provider>
      )}
    </Box>
  );
}

function PreviewText({ node, ...rest }: { node: TextNode } & PreviewNodeBaseProps) {
  const { t } = useTranslation('emailBuilder');
  const { handlers, outlineSx, hovered, selected } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const rawContent = node.props.content ?? '';
  const isHtml = node.props.format === 'html';
  const [editing, setEditing] = useState(false);
  const editRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canEdit = !!rest.onUpdateNodeContent;
  // Frozen content ensures dangerouslySetInnerHTML stays put while editing —
  // typing-debounce commits would otherwise re-apply the prop and reset the
  // cursor on every keystroke.
  const frozenContentRef = useRef('');

  const readEditedContent = (el: HTMLElement): { content: string; format: 'plain' | 'html' } => {
    const html = el.innerHTML;
    const hasMarkup = /<(a|strong|em|b|i|u|s|br|span|font)\b/i.test(html);
    return hasMarkup ? { content: html, format: 'html' } : { content: el.innerText, format: 'plain' };
  };

  const commitFromDom = () => {
    if (!editRef.current || !rest.onUpdateNodeContent) return;
    const { content, format } = readEditedContent(editRef.current);
    rest.onUpdateNodeContent(node.id, content, format);
  };

  const commitEdit = () => {
    commitFromDom();
    setEditing(false);
  };

  const [linkInputOpen, setLinkInputOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  // Live selection inside the editable. Tracked via selectionchange so we
  // always have the *last in-editable selection* even if hovering the toolbar
  // (or any focus / DOM glitch) momentarily drops it.
  const savedRangeRef = useRef<Range | null>(null);

  // Mirrors the user's intended commit so it can fire from outside-click.
  const wantCommitRef = useRef(false);

  // Track the last in-editable selection across transient focus/DOM glitches
  // (e.g. hovering the toolbar Popper). selectionchange re-applies the saved
  // Range if the live selection drifts outside the editable, so toolbar
  // commands always run on the user's intended target.
  useEffect(() => {
    if (!editing) {
      savedRangeRef.current = null;
      wantCommitRef.current = false;
      setLinkInputOpen(false);
      setLinkUrl('');
      return;
    }

    const onSel = () => {
      const sel = window.getSelection();
      const root = editRef.current;
      if (!sel || !root) return;
      // When user is committing (pointerdown outside) we let the selection go.
      if (wantCommitRef.current) return;
      if (sel.rangeCount === 0) {
        const r = savedRangeRef.current;
        if (r && root.contains(r.startContainer) && root.contains(r.endContainer)) {
          sel.addRange(r);
        }
        return;
      }
      const range = sel.getRangeAt(0);
      const inside = root.contains(range.startContainer) && root.contains(range.endContainer);
      if (inside) {
        savedRangeRef.current = range.cloneRange();
        return;
      }
      const r = savedRangeRef.current;
      if (r && root.contains(r.startContainer) && root.contains(r.endContainer)) {
        sel.removeAllRanges();
        sel.addRange(r);
      }
    };

    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, [editing]);

  // Real outside-click commit. The previous onBlur-based exit fires whenever
  // focus shifts (including transient losses caused by hovering MUI widgets
  // that mount/unmount), which made the selection appear to "vanish" because
  // editing flipped off mid-interaction. A pointerdown listener only commits
  // on an actual click outside the editable + toolbar.
  useEffect(() => {
    if (!editing) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const inEditable = !!editRef.current?.contains(target);
      const inToolbar = !!toolbarRef.current?.contains(target);
      // Link sub-mode dismiss: any pointerdown outside the toolbar (including
      // back into the editable) cancels the in-progress link entry and
      // reverts to the standard formatting buttons.
      if (linkInputOpen && !inToolbar) {
        setLinkInputOpen(false);
        setLinkUrl('');
        if (inEditable) return;
      }
      if (inEditable) return;
      if (inToolbar) return;
      // Tell the selectionchange guard to stop re-applying the saved range —
      // user is intentionally clicking out.
      wantCommitRef.current = true;
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
      commitEdit();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, linkInputOpen]);

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    if (!range) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const exec = (cmd: string, value?: string) => {
    if (!editRef.current) return;
    editRef.current.focus({ preventScroll: true });
    restoreSelection();
    document.execCommand(cmd, false, value);
    commitFromDom();
  };

  const openLinkInput = () => {
    const range = savedRangeRef.current;
    const anchor = range?.startContainer
      ? findAncestorAnchor(range.startContainer, editRef.current!)
      : null;
    setLinkUrl(anchor?.getAttribute('href') ?? '');
    setLinkInputOpen(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url || !editRef.current) return;
    editRef.current.focus({ preventScroll: true });
    restoreSelection();
    document.execCommand('createLink', false, url);
    commitFromDom();
    setLinkInputOpen(false);
    setLinkUrl('');
  };

  const removeLink = () => {
    if (!editRef.current) return;
    editRef.current.focus({ preventScroll: true });
    restoreSelection();
    document.execCommand('unlink');
    commitFromDom();
  };

  const handleColor = (hex: string) => exec('foreColor', hex);

  const effectiveAlign =
    (rest.isMobile && node.style?.mobile?.align) || node.style?.align || 'left';
  const baseSx = {
    color: node.style?.color || '#111827',
    fontSize: node.style?.fontSize || (node.props.role === 'headline' ? 28 : 14),
    fontWeight: node.style?.fontWeight || (node.props.role === 'headline' ? 700 : 400),
    textAlign: effectiveAlign,
    lineHeight: node.style?.lineHeight || 1.5,
    letterSpacing: node.style?.letterSpacing,
    fontFamily: node.style?.fontFamily,
    p: spacingToCss(node.style?.padding) || '10px',
    m: 0,
    minHeight: 20,
  };

  const displayedHtml = editing
    ? frozenContentRef.current
    : (rawContent || `<span style="color:${TEXT_DIM};font-style:italic">${t('emailBuilderPreview.text.empty', 'Empty text')}</span>`);

  // Manage innerHTML imperatively so React never replaces the text nodes on
  // re-render. With dangerouslySetInnerHTML, every parent re-render produced
  // a childList mutation (remove text node + add identical text node), which
  // collapsed the native contentEditable selection mid-interaction.
  useLayoutEffect(() => {
    const el = editRef.current;
    if (!el) return;
    if (editing) return; // user owns the DOM while editing — never overwrite.
    if (el.innerHTML !== displayedHtml) {
      el.innerHTML = displayedHtml;
    }
  }, [editing, displayedHtml]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={editRef}
        {...handlers}
        contentEditable={editing}
        suppressContentEditableWarning
        spellCheck={false}
        // Opt out of writing-assistant browser extensions that inject
        // overlay nodes into contentEditable elements (Grammarly,
        // LanguageTool, DeepL, ProWritingAid, etc.). Their childList
        // mutations collapse native selection mid-interaction.
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
        data-lt-active="false"
        // Commit handled by the document-level pointerdown listener above —
        // onBlur is too eager (fires on transient focus changes like hovering
        // MUI widgets) and would prematurely exit editing.
        onKeyDown={editing ? (e) => {
          if (e.key === 'Escape') { e.preventDefault(); setEditing(false); return; }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitEdit(); return; }
          if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
          commitTimerRef.current = setTimeout(commitFromDom, 600);
        } : undefined}
        onMouseDown={(e) => {
          // Enter edit mode on the second mousedown of a double-click, BEFORE
          // the browser performs its word-selection on mouseup. Otherwise the
          // contentEditable=true transition collapses the fresh selection and
          // toolbar commands like Bold/Link end up running on a caret.
          if (editing || !canEdit || e.detail !== 2 || e.button !== 0) return;
          e.stopPropagation();
          rest.onSelectNode(node.id);
          frozenContentRef.current = rawContent;
          flushSync(() => setEditing(true));
          editRef.current?.focus({ preventScroll: true });
          // Browser will now perform word-select on the editable element on
          // the corresponding mouseup; selection persists naturally.
        }}
        onDoubleClick={(e) => {
          // Fallback path (e.g. touch/synthetic events that didn't surface a
          // detail===2 mousedown). Use the saved-range restore strategy.
          if (editing || !canEdit) return;
          e.stopPropagation();
          rest.onSelectNode(node.id);
          frozenContentRef.current = rawContent;
          const sel = window.getSelection();
          const savedRange = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
          flushSync(() => setEditing(true));
          const el = editRef.current;
          if (!el) return;
          el.focus({ preventScroll: true });
          if (savedRange && el.contains(savedRange.startContainer) && el.contains(savedRange.endContainer)) {
            const s = window.getSelection();
            s?.removeAllRanges();
            s?.addRange(savedRange);
          }
        }}
        sx={{
          ...baseSx,
          ...(editing
            ? { outline: `2px solid ${INDIGO}`, outlineOffset: -2, bgcolor: 'rgba(99,102,241,0.04)' }
            : outlineSx),
          whiteSpace: 'pre-wrap',
          cursor: editing || canEdit ? 'text' : 'default',
          '& a': { color: '#2563eb', textDecoration: 'underline' },
          '&:focus': { outline: `2px solid ${INDIGO}`, outlineOffset: -2 },
          '&:focus-visible': { outline: `2px solid ${INDIGO}`, outlineOffset: -2 },
          // Keep the highlight bright + visible even when the editable loses
          // keyboard focus (e.g. hovering MUI Popper toolbar). Browsers dim
          // the default ::selection color when focus drops, which reads as
          // "the highlight disappeared".
          '& ::selection': { backgroundColor: 'rgba(99,102,241,0.40)', color: 'inherit' },
          '&::selection': { backgroundColor: 'rgba(99,102,241,0.40)', color: 'inherit' },
        }}
      />
      {editing && editRef.current && (
        <Popper
          open
          anchorEl={editRef.current}
          placement="top-start"
          // Overlap the editable's top edge by 2px so the toolbar and the
          // text share a continuous hover surface — no gap for the cursor
          // to cross, which avoids the selection-loss path users hit when
          // moving the mouse "outside" the editable to reach Bold/Link.
          modifiers={[{ name: 'offset', options: { offset: [0, -2] } }]}
          style={{ zIndex: 1500 }}
        >
          <Paper
            ref={toolbarRef}
            elevation={6}
            // Single delegating mousedown handler. Native mousedown fires on
            // the actual visible DOM target before React reconciliation can
            // swap nodes, and `closest('[data-action]')` resolves through the
            // SVG/icon descendants too — so we don't depend on per-button refs
            // or React event delegation, both of which were unreliable when
            // the parent re-renders frequently.
            //
            // Triggering on mousedown (not click) also avoids the failure
            // where mouseup lands on a different element than mousedown.
            // preventDefault keeps focus + selection in the editable.
            onMouseDown={(e) => {
              const target = e.target as HTMLElement | null;
              if (target instanceof HTMLInputElement) return; // color picker, link textfield
              e.preventDefault();
              const actionEl = target?.closest('[data-action]') as HTMLElement | null;
              if (!actionEl) return;
              const action = actionEl.getAttribute('data-action');
              switch (action) {
                case 'bold': exec('bold'); break;
                case 'italic': exec('italic'); break;
                case 'underline': exec('underline'); break;
                case 'strikeThrough': exec('strikeThrough'); break;
                case 'link-open': openLinkInput(); break;
                case 'link-remove': removeLink(); break;
              }
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.5, py: 0.25, borderRadius: 1, bgcolor: '#1f2937' }}
          >
            {linkInputOpen ? (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 0.5 }}>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="https://..."
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                    if (e.key === 'Escape') { e.preventDefault(); setLinkInputOpen(false); editRef.current?.focus(); }
                  }}
                  sx={{
                    '& .MuiInputBase-root': { color: '#fff', fontSize: 12, height: 28, bgcolor: '#374151' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                    minWidth: 220,
                  }}
                />
                <Button size="small" variant="contained" onClick={applyLink} sx={{ textTransform: 'none', fontSize: 11, py: 0.25, minWidth: 0 }}>{t('emailBuilderPreview.text.toolbar.apply', 'Apply')}</Button>
              </Stack>
            ) : (
              <>
                <ToolbarBtn title={t('emailBuilderPreview.text.toolbar.bold', 'Bold')} action="bold"><FormatBoldIcon sx={{ fontSize: 16 }} /></ToolbarBtn>
                <ToolbarBtn title={t('emailBuilderPreview.text.toolbar.italic', 'Italic')} action="italic"><FormatItalicIcon sx={{ fontSize: 16 }} /></ToolbarBtn>
                <ToolbarBtn title={t('emailBuilderPreview.text.toolbar.underline', 'Underline')} action="underline"><FormatUnderlinedIcon sx={{ fontSize: 16 }} /></ToolbarBtn>
                <ToolbarBtn title={t('emailBuilderPreview.text.toolbar.strikethrough', 'Strikethrough')} action="strikeThrough"><StrikethroughIcon sx={{ fontSize: 16 }} /></ToolbarBtn>
                <Divider orientation="vertical" flexItem sx={{ borderColor: '#374151', mx: 0.25 }} />
                <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }} title={t('emailBuilderPreview.text.toolbar.color', 'Color')}>
                  <FormatColorTextIcon sx={{ fontSize: 16, color: '#fff', p: 0.5 }} />
                  <input
                    type="color"
                    onChange={(e) => handleColor(e.target.value)}
                    style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                  />
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: '#374151', mx: 0.25 }} />
                <ToolbarBtn title={t('emailBuilderPreview.text.toolbar.insertLink', 'Insert link')} action="link-open"><LinkIcon sx={{ fontSize: 16 }} /></ToolbarBtn>
                <ToolbarBtn title={t('emailBuilderPreview.text.toolbar.removeLink', 'Remove link')} action="link-remove"><LinkOffIcon sx={{ fontSize: 16 }} /></ToolbarBtn>
              </>
            )}
          </Paper>
        </Popper>
      )}
      {!editing && hovered && canEdit && !selected && (
        <Box sx={{
          position: 'absolute',
          bottom: 4,
          right: 6,
          fontSize: 9,
          fontWeight: 600,
          color: INDIGO,
          bgcolor: 'rgba(99,102,241,0.1)',
          px: '5px',
          py: '1px',
          borderRadius: '3px',
          pointerEvents: 'none',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}>
          {t('emailBuilderPreview.text.doubleClickToEdit', 'Double-click to edit')}
        </Box>
      )}
    </Box>
  );
}

function ToolbarBtn({ title, action, children }: { title: string; action: string; children: React.ReactNode }) {
  // Stateless button. Dispatch happens in a single delegating mousedown
  // handler on the parent Paper — see EmailBuilderPreview.tsx Paper onMouseDown.
  // We intentionally do NOT attach handlers per-button: when the parent
  // re-renders frequently, ref-based listeners can end up bound to a detached
  // node and the visible button becomes inert. data-action on a single
  // delegator is immune to that.
  return (
    <Box
      component="button"
      type="button"
      data-action={action}
      title={title}
      sx={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#fff',
        p: 0.5,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        '&:hover': { bgcolor: '#374151' },
        // Make the icon's <svg>/<path> non-interactive so the click target
        // is always the button itself.
        '& svg, & svg *': { pointerEvents: 'none' },
      }}
    >
      {children}
    </Box>
  );
}

function findAncestorAnchor(start: Node | null, stopAt: Node): HTMLAnchorElement | null {
  let n: Node | null = start;
  while (n && n !== stopAt) {
    if (n.nodeType === 1 && (n as Element).tagName === 'A') return n as HTMLAnchorElement;
    n = n.parentNode;
  }
  return null;
}

// Mirrors EmailDocumentCompiler.resolveImage: returns either a resolved URL
// or a "render-as-logotype" instruction so the preview matches the compiled
// output when a brand asset is missing.
type EditorImageResolution =
  | { kind: 'url'; src: string }
  | { kind: 'logotype'; label: string }
  | { kind: 'none' };

// Iconify URIs like "iconify:phosphor:truck?color=111111&size=48" are the
// section-library authoring form for icons. Preview renders them through
// @iconify/react's <Icon> (inline SVG, cached in-memory). Backend compile
// swaps them to cached PNG CDN URLs so email clients — which block SVG —
// still render them. Source of truth is the URI; both paths branch from it.
interface ParsedIconifyUri {
  id: string;
  color?: string;
  size?: number;
}

function parseIconifyUri(src: string): ParsedIconifyUri | null {
  if (!src.startsWith('iconify:')) return null;
  const rest = src.slice('iconify:'.length);
  if (!rest) return null;
  const qIdx = rest.indexOf('?');
  const id = qIdx === -1 ? rest : rest.slice(0, qIdx);
  if (!id.includes(':')) return null;
  const params = new URLSearchParams(qIdx === -1 ? '' : rest.slice(qIdx + 1));
  const color = params.get('color') ?? undefined;
  const sizeRaw = params.get('size');
  const sizeNum = sizeRaw ? parseInt(sizeRaw, 10) : NaN;
  return {
    id,
    color: color ? (color.startsWith('#') ? color : `#${color}`) : undefined,
    size: Number.isFinite(sizeNum) ? sizeNum : undefined,
  };
}

function resolveEditorImage(
  node: ImageNode,
  brandLogoUrl?: string,
  brandName?: string,
): EditorImageResolution {
  if (node.props.variant === 'logo') {
    return brandLogoUrl
      ? { kind: 'url', src: brandLogoUrl }
      : { kind: 'logotype', label: brandName || 'Logo' };
  }
  const src = node.props.src;
  if (typeof src === 'object' && src && '$brandAsset' in src) {
    const assetKey = (src as { $brandAsset: string }).$brandAsset;
    if (assetKey === 'logo' && brandLogoUrl) return { kind: 'url', src: brandLogoUrl };
    return { kind: 'logotype', label: brandName || assetKey };
  }
  return typeof src === 'string' ? { kind: 'url', src } : { kind: 'none' };
}

function PreviewImage({ node, ...rest }: { node: ImageNode } & PreviewNodeBaseProps) {
  const { t } = useTranslation('emailBuilder');
  const { handlers, outlineSx, hovered } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const resolved = resolveEditorImage(node, rest.brandLogoUrl, rest.brandName);
  const canPick = !!rest.onPickImage;

  const dblClickHandler = canPick
    ? (e: React.MouseEvent) => { e.stopPropagation(); rest.onPickImage!(node.id); }
    : undefined;

  if (resolved.kind === 'logotype') {
    return (
      <Box
        {...handlers}
        onDoubleClick={dblClickHandler}
        sx={{
          textAlign: node.style?.align || 'center',
          p: spacingToCss(node.style?.padding) || '10px',
          color: '#111827',
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '0.02em',
          position: 'relative',
          cursor: canPick ? 'pointer' : 'default',
          ...outlineSx,
        }}
      >
        {resolved.label}
      </Box>
    );
  }

  const imgSrc = resolved.kind === 'url' ? resolved.src : undefined;
  const iconify = imgSrc ? parseIconifyUri(imgSrc) : null;
  if (iconify) {
    const iconWidth = iconify.size ?? (typeof node.style?.width === 'number' ? node.style.width : 48);
    return (
      <Box
        {...handlers}
        onDoubleClick={dblClickHandler}
        sx={{
          textAlign: node.style?.align || 'center',
          p: spacingToCss(node.style?.padding),
          position: 'relative',
          cursor: canPick ? 'pointer' : 'default',
          lineHeight: 0,
          ...outlineSx,
        }}
      >
        <Icon
          icon={iconify.id}
          width={iconWidth}
          height={iconWidth}
          color={iconify.color}
        />
      </Box>
    );
  }
  const hasExplicitHeight = typeof node.style?.height === 'number';
  // Preview parity with the server-side fit pipeline: when the author set
  // `aspectRatio` + `fit`, browsers can enforce it with CSS `aspect-ratio` +
  // `object-fit` so the preview tiles come out identical-size without
  // running sharp. Without this, grid/gallery sections render each image at
  // its natural aspect and the preview looks inconsistent.
  const aspectCss = node.style?.aspectRatio
    ? node.style.aspectRatio.replace(':', ' / ')
    : undefined;
  const fitMode = node.style?.fit;
  const objectFitCss: React.CSSProperties['objectFit'] = hasExplicitHeight || aspectCss
    ? fitMode === 'blur-pad'
      ? 'contain'
      : 'cover'
    : undefined;
  return (
    <Box
      {...handlers}
      onDoubleClick={dblClickHandler}
      sx={{
        textAlign: node.style?.align || 'center',
        p: spacingToCss(node.style?.padding),
        position: 'relative',
        cursor: canPick ? 'pointer' : 'default',
        ...outlineSx,
      }}
    >
      <img
        src={imgSrc || 'https://placehold.co/600x300/e5e7eb/6b7280?text=Image'}
        alt={node.props.alt}
        style={{
          display: 'inline-block',
          // An inline-block sits on the text baseline, so the line box's
          // descender space shows up as a few px of background under every
          // image. MJML ships `display:block` and has no such gap, so without
          // this the canvas draws seams between stacked images that the email
          // does not have. `middle` keeps `text-align` centering working, which
          // a bare `display:block` would lose.
          verticalAlign: 'middle',
          maxWidth: '100%',
          width:
            node.style?.width === '100%'
              ? '100%'
              : typeof node.style?.width === 'number'
                ? `${node.style.width}px`
                : '100%',
          height: hasExplicitHeight ? `${node.style!.height}px` : 'auto',
          aspectRatio: !hasExplicitHeight ? aspectCss : undefined,
          objectFit: objectFitCss,
          borderRadius: cornerRadiusCss(node.style?.borderRadius),
        }}
      />
      {hovered && canPick && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.35)',
          borderRadius: cornerRadiusCss(node.style?.borderRadius),
          pointerEvents: 'none',
        }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.95)',
            color: '#111827',
            fontSize: 11,
            fontWeight: 700,
            px: 1.5,
            py: '5px',
            borderRadius: '6px',
            letterSpacing: '0.04em',
          }}>
            {t('emailBuilderPreview.image.doubleClickToReplace', 'Double-click to replace')}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function PreviewButton({ node, ...rest }: { node: ButtonNode } & PreviewNodeBaseProps) {
  const { handlers, outlineSx } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const resolveColor = useResolveColor();
  const surroundingBg = useContext(SurroundingBackgroundContext);
  // `style.padding` maps to MJML mj-button's `padding` attribute — the OUTER
  // spacing around the button, not the label's inner text padding. MJML uses
  // its own `inner-padding` default (10px 25px) for the label. Preview must
  // match so Button Grid / Button Stack with padding:0 render flush without
  // the label hugging the button edge.
  const innerPx = '25px';
  const innerPy = '10px';
  const resolvedBg = resolveColor(node.style?.backgroundColor);
  // When the button's bg matches the surface behind it, the fill disappears.
  // Fall back to an outlined treatment using a contrasting color so the
  // button still reads as a button in the editor.
  const matchesSurround =
    !!resolvedBg &&
    !!surroundingBg &&
    normalizeHex(resolvedBg) === normalizeHex(surroundingBg);
  const hasExplicitBorder = !!node.style?.border;
  const fallbackBorderColor = contrastTextOnHex(surroundingBg);
  const useFallbackOutline = matchesSurround && !hasExplicitBorder;
  return (
    <Box
      {...handlers}
      sx={{
        textAlign: node.style?.align || 'center',
        p: spacingToCss(node.style?.padding) ?? '10px',
        ...outlineSx,
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          boxSizing: 'border-box',
          bgcolor: resolvedBg || '#111827',
          color: resolveColor(node.style?.color) || '#FFFFFF',
          ...bordersToCss(node.style, resolveColor),
          ...(useFallbackOutline && { border: `1px solid ${fallbackBorderColor}` }),
          borderRadius: cornerRadiusCss(node.style?.borderRadius) || '6px',
          fontSize: node.style?.fontSize || 14,
          fontWeight: node.style?.fontWeight || 600,
          px: innerPx,
          py: innerPy,
          width: node.style?.fullWidth ? '100%' : 'auto',
          textAlign: 'center',
        }}
      >
        {node.props.label}
      </Box>
    </Box>
  );
}

function PreviewDivider({ node, ...rest }: { node: DividerNode } & PreviewNodeBaseProps) {
  const { handlers, outlineSx } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  return (
    <Box {...handlers} sx={{ p: spacingToCss(node.style?.padding) || '10px 0', ...outlineSx }}>
      <Box
        sx={{
          height: node.style?.thickness ?? 2,
          bgcolor: node.style?.color || '#E5E7EB',
          width: node.style?.width || '100%',
          mx: 'auto',
        }}
      />
    </Box>
  );
}

function PreviewSpacer({ node, ...rest }: { node: SpacerNode } & PreviewNodeBaseProps) {
  const { selected, hovered, handlers } = useSelectableProps(
    node,
    rest.selectedNodeId,
    rest.onSelectNode,
    rest.hoveredNodeId,
    rest.onHoverNode
  );
  return (
    <Box
      {...handlers}
      sx={{
        height: node.props.height,
        bgcolor: selected ? INDIGO_SOFT : 'transparent',
        outline: selected ? `1px solid ${INDIGO}` : hovered ? `1px dashed ${DROP_DASH}` : 'none',
      }}
    />
  );
}

function PreviewSocial({ node, brandSocialIcons, ...rest }: { node: SocialNode } & PreviewNodeBaseProps) {
  const { handlers, outlineSx } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const size = node.style?.iconSize || 28;
  // Resolution chain mirrors the compiler exactly: link.iconUrl override
  // beats brand profile beats CDN default. Iconify is the last-resort
  // fallback for an unknown platform string (shouldn't happen with the
  // typed union but the data is loose JSON in storage).
  return (
    <Stack
      direction="row"
      spacing={1.5}
      {...handlers}
      sx={{
        justifyContent: alignToJustify(node.style?.align),
        p: spacingToCss(node.style?.padding) || '10px',
        ...outlineSx,
      }}
    >
      {node.props.links.map((link, i) => {
        const def = SOCIAL_ICONS[link.platform];
        const src =
          link.iconUrl ||
          brandSocialIcons?.[link.platform] ||
          (def ? socialIconUrl(link.platform) : undefined);
        return (
          <Box
            key={`${link.platform}-${i}`}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {src ? (
              <img src={src} alt={def?.label ?? link.platform} width={size} height={size} style={{ display: 'block' }} />
            ) : def ? (
              <Icon icon={def.iconifyId} width={size} height={size} />
            ) : (
              <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>
                {link.platform.charAt(0).toUpperCase()}
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function PreviewNavbar({ node, ...rest }: { node: NavbarNode } & PreviewNodeBaseProps) {
  const { handlers, outlineSx } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  return (
    <Stack
      direction="row"
      spacing={2.5}
      {...handlers}
      sx={{
        justifyContent: alignToJustify(node.style?.align),
        p: spacingToCss(node.style?.padding) || '10px',
        ...outlineSx,
      }}
    >
      {node.props.links.map((link) => (
        <Link
          key={`${link.label}-${link.href}`}
          href={link.href}
          underline="hover"
          onClick={(e) => e.preventDefault()}
          sx={{
            color: node.style?.color || '#111827',
            fontSize: node.style?.fontSize || 14,
            fontWeight: 500,
            fontFamily: node.style?.fontFamily,
          }}
        >
          {link.label}
        </Link>
      ))}
    </Stack>
  );
}

function PreviewProductFeed({ node, brandPrimaryColor, ...rest }: { node: ProductFeedNode } & PreviewNodeBaseProps) {
  const { t } = useTranslation('emailBuilder');
  const { handlers, outlineSx } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const perRow = node.props.layout === 'grid-3' ? 3 : node.props.layout === 'grid-2' ? 2 : 1;
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const clientId = activeClient?.id ?? '';

  const manualIds =
    node.props.source?.kind === 'manual' ? (node.props.source as { kind: 'manual'; productIds: string[] }).productIds : [];

  // Only hit the API for real ids; blanks resolve to a placeholder locally.
  const realIds = manualIds.filter(id => id !== BLANK_PRODUCT_ID);
  const { data: productData, isLoading } = useV1ProductControllerGetByVariantIds(
    orgId,
    clientId,
    { ids: realIds.join(',') },
    { query: { enabled: !!orgId && !!clientId && realIds.length > 0 } },
  );

  const fetchedProducts = productData?.data ?? [];
  type FetchedProduct = (typeof fetchedProducts)[number];
  const productById = new Map<string, FetchedProduct>(fetchedProducts.map(p => [p.variantId, p]));
  const slots: Array<{ blank: boolean; product?: FetchedProduct }> =
    manualIds.length > 0
      ? manualIds.map(id =>
          id === BLANK_PRODUCT_ID ? { blank: true } : { blank: false, product: productById.get(id) },
        )
      : Array.from({ length: Math.max(1, node.props.limit ?? 1) }, () => ({ blank: true }));

  const include = new Set(node.props.itemTemplate.include);
  const textAlign = (node.style?.textAlign ?? 'left') as 'left' | 'center' | 'right';
  const buttonAlign = (node.style?.buttonAlign ?? 'left') as 'left' | 'center' | 'right';
  const buttonJustify =
    buttonAlign === 'center' ? 'center' : buttonAlign === 'right' ? 'flex-end' : 'flex-start';
  const ratio = (node.props.itemTemplate.imageAspectRatio ?? '1:1').replace(':', ' / ');

  return (
    <Box
      {...handlers}
      sx={{
        // Product-feed is a section child peer to group/column; claim the full
        // row so siblings (headings, descriptions in a column) don't share width.
        flex: '1 0 100%',
        width: '100%',
        boxSizing: 'border-box',
        p: spacingToCss(node.style?.padding) || '10px',
        ...outlineSx,
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`,
            gap: `${node.style?.gap ?? 12}px`,
          }}
        >
          {slots.map((slot, index) => {
            const product = slot.product;
            const imageUrl = slot.blank
              ? undefined
              : product?.variantImageUrl ?? product?.productImages?.[0]?.src;
            const title = slot.blank ? t('emailBuilderPreview.productFeed.productTitle', 'Product title') : (product?.productTitle ?? t('emailBuilderPreview.productFeed.selectedProduct', 'Selected product'));
            const price = slot.blank
              ? '$0.00'
              : product?.variantPrice != null
                ? `$${Number(product.variantPrice).toFixed(2)}`
                : '$—';

            return (
              <Box
                key={index}
                sx={{
                  bgcolor: node.style?.cardBackgroundColor || '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                {include.has('image') ? (
                  imageUrl ? (
                    <Box
                      component="img"
                      src={imageUrl}
                      sx={{ width: '100%', aspectRatio: ratio, objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <Box sx={{ aspectRatio: ratio, bgcolor: '#E5E7EB' }} />
                  )
                ) : null}
                <Stack spacing={0.5} sx={{ p: 1.5 }}>
                  {include.has('title') ? (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, textAlign }}>{title}</Typography>
                  ) : null}
                  {include.has('price') ? (
                    <Typography sx={{ fontSize: 11, color: '#6B7280', textAlign }}>{price}</Typography>
                  ) : null}
                  {include.has('cta') ? (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: buttonJustify }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          bgcolor: brandPrimaryColor ?? '#111827',
                          color: '#FFFFFF',
                          fontSize: 14,
                          fontWeight: 700,
                          px: 3,
                          py: 1.5,
                          borderRadius: 1,
                          textAlign: 'center',
                        }}
                      >
                        {node.props.itemTemplate.ctaLabel || 'Shop Now'}
                      </Box>
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// Measures actual layout width of a container and returns the ratio against
// the canvas doc's natural width. CanvasKit renders at 1:1 in
// `disableAutoFit + lockViewport` mode, so we apply CSS transform: scale to
// the inner box to make the canvas fit the rendered email viewport
// (e.g. 375 mobile, 600 desktop). clientWidth is unaffected by the CSS
// transform we apply to children, so this measurement stays stable.
function useContainerScale(docWidth: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || docWidth <= 0) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / docWidth));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [docWidth]);
  return { ref, scale };
}

/** Root-level canvas object: renders as an interactive CanvasKit area in the email flow. */
function PreviewRootCanvasObject({
  node,
  activeCanvasSectionId,
  canvasEditorRef,
  onCanvasDocumentChange,
  ...rest
}: { node: CanvasObject } & PreviewNodeBaseProps) {
  const { outlineSx } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const { width, height } = node.props.transform;
  const isActive = activeCanvasSectionId === node.id;
  const hasStore = useHasEditorStore();
  const { ref: scaleRef, scale } = useContainerScale(width);

  const doc: SceneDocument = normalizeSceneDocument({
    version: 2,
    width,
    height,
    nodes: { [node.id]: node as any },
    rootChildren: [node.id],
  });

  return (
    <Box
      ref={scaleRef}
      {...(isActive ? { 'data-canvas-active': true } : {})}
      sx={{
        position: 'relative',
        height: height * scale,
        width: '100%',
        flexShrink: 0,
        overflow: 'hidden',
        ...outlineSx,
      }}
    >
      <Box
        sx={{
          width,
          height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {isActive && hasStore ? (
          <CanvasKitEditor
            ref={canvasEditorRef}
            initialDocument={doc}
            onDocumentChange={onCanvasDocumentChange ?? (() => {})}
            onSelectionChange={() => {}}
            disableAutoFit
            lockViewport
          />
        ) : (
          <CanvasKitEditorProvider key={node.id} initialDocument={doc}>
            <CanvasKitEditor
              initialDocument={doc}
              onDocumentChange={() => {}}
              onSelectionChange={() => rest.onSelectNode(node.id)}
              disableAutoFit
              lockViewport
            />
          </CanvasKitEditorProvider>
        )}
      </Box>
    </Box>
  );
}

/**
 * Non-active canvas with saved content: renders a CPU-rendered PNG snapshot.
 * No WebGL context is allocated — only the actively-edited canvas section
 * uses real CanvasKit/WebGL. See web/src/lib/canvas-snapshot.ts for details.
 */
function ReadOnlyCanvasSection({
  node,
  handlers,
  outlineSx,
  toolbar,
}: {
  node: CanvasSectionNode;
  handlers: Record<string, unknown>;
  outlineSx: Record<string, unknown>;
  toolbar?: React.ReactNode;
}) {
  const { width = 600, height = 300 } = node.props.scene ?? {};
  const picker = useDynamicPicker();
  const { dataUrl } = useCanvasSnapshot({
    canvasJson: node.props.canvasJson ?? null,
    width,
    height,
    brand: picker?.brand ?? null,
    slotResolver: picker?.previewSlotResolver,
  });
  const { ref: scaleRef, scale } = useContainerScale(width);
  return (
    <Box sx={{ position: 'relative', width: '100%', flexShrink: 0 }}>
      {toolbar}
      <Box
        ref={scaleRef}
        sx={{
          position: 'relative',
          height: height * scale,
          width: '100%',
          overflow: 'hidden',
          ...outlineSx,
        }}
      >
        {dataUrl && (
          <Box
            component="img"
            src={dataUrl}
            alt=""
            draggable={false}
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          />
        )}
        {/* Transparent overlay captures clicks to select/activate this section */}
        <Box
          {...handlers}
          sx={{
            position: 'absolute',
            inset: 0,
            cursor: 'pointer',
          }}
        />
      </Box>
    </Box>
  );
}

// ─── Canvas section vertical resize ─────────────────────────────────────────

const HEIGHT_SNAP_POINTS = [200, 300, 400, 500, 600, 800];
const HEIGHT_SNAP_TOLERANCE = 8;
const MIN_CANVAS_HEIGHT = 100;

function snapHeight(h: number): number {
  for (const s of HEIGHT_SNAP_POINTS) {
    if (Math.abs(h - s) < HEIGHT_SNAP_TOLERANCE) return s;
  }
  return Math.round(h);
}

function CanvasHeightResizeHandle({
  scale,
  onPreview,
  onCommit,
}: {
  scale: number;
  onPreview: (height: number | null) => void;
  onCommit: (height: number) => void;
}) {
  const docWidth = useEditorStore((s) => s.doc.width);
  const docHeight = useEditorStore((s) => s.doc.height);

  const [dragging, setDragging] = useState(false);
  const stateRef = useRef({ startY: 0, startHeight: 0, startWidth: 600, lastHeight: 0 });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    stateRef.current = {
      startY: e.clientY,
      startHeight: docHeight,
      startWidth: docWidth,
      lastHeight: docHeight,
    };
    setDragging(true);

    const onMove = (me: MouseEvent) => {
      const { startY, startHeight: sh } = stateRef.current;
      const dyCanvas = (me.clientY - startY) / Math.max(scale, 0.01);
      const next = Math.max(MIN_CANVAS_HEIGHT, snapHeight(sh + dyCanvas));
      stateRef.current.lastHeight = next;
      onPreview(next);
    };
    const onUp = () => {
      setDragging(false);
      onPreview(null);
      onCommit(stateRef.current.lastHeight);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [docHeight, docWidth, scale, onPreview, onCommit]);

  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 8,
        cursor: 'ns-resize',
        zIndex: 5,
        '&:hover .canvas-resize-line': { opacity: 1, bgcolor: INDIGO },
        ...(dragging && { '& .canvas-resize-line': { opacity: 1, bgcolor: INDIGO } }),
      }}
    >
      <Box
        className="canvas-resize-line"
        sx={{
          position: 'absolute',
          left: '10%',
          right: '10%',
          bottom: 1,
          height: 2,
          bgcolor: 'rgba(99,102,241,0.4)',
          borderRadius: 1,
          opacity: 0,
          transition: 'opacity 100ms ease, background-color 100ms ease',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

/** Active canvas: subscribes to live store height so container auto-grows with content. */
function ActiveCanvasSection({
  node,
  canvasEditorRef,
  onCanvasDocumentChange,
  toolbar,
}: {
  node: CanvasSectionNode;
  canvasEditorRef?: React.RefObject<CanvasKitEditorHandle | null>;
  onCanvasDocumentChange?: () => void;
  toolbar?: React.ReactNode;
}) {
  const { width = 600, height = 300 } = node.props.scene ?? {};
  const liveHeight = useEditorStore((s) => s.doc.height);
  const liveWidth = useEditorStore((s) => s.doc.width);
  const updateDocumentSize = useEditorStore((s) => s.updateDocumentSize);
  // Hugging artboards own their height (it tracks content via the layout
  // pass); a manual drag handle would fight auto-grow. Fixed scenes keep it.
  const isHuggingArtboard = useEditorStore((s) => {
    const rootId = s.doc.rootChildren.length === 1 ? s.doc.rootChildren[0] : null;
    const root = rootId ? s.doc.nodes[rootId] : null;
    return !!root && root.type === 'FRAME' && root.layoutSizingVertical === 'HUG';
  });
  const [previewHeight, setPreviewHeight] = useState<number | null>(null);
  const displayHeight = previewHeight ?? liveHeight;
  const initialDoc: SceneDocument = normalizeSceneDocument(
    node.props.canvasJson ?? { version: 2, width, height, nodes: {}, rootChildren: [] }
  );
  const { ref: scaleRef, scale } = useContainerScale(width);

  // CanvasKitEditor seeds itself once from initialDocument and then owns its
  // internal scene state. When an outside actor (AI remix proposal, undo/redo,
  // server load) replaces the email document, props.canvasJson changes but
  // the editor never re-reads it — so the canvas keeps showing the pre-apply
  // scene until the page is reloaded. Watch the doc-store's externalApplyCount
  // (bumped only on those whole-doc swaps, NOT on regular updateNode writes
  // that originate from the editor itself) and call loadDocument imperatively
  // when it changes.
  const externalApplyCount = useDocStore((s) => s.externalApplyCount);
  const lastSeededApplyCountRef = useRef(externalApplyCount);
  useEffect(() => {
    if (externalApplyCount === lastSeededApplyCountRef.current) return;
    lastSeededApplyCountRef.current = externalApplyCount;
    const handle = canvasEditorRef?.current;
    if (!handle) return;
    const nextScene = node.props.canvasJson;
    if (!nextScene) return;
    handle.loadDocument(normalizeSceneDocument(nextScene));
  }, [externalApplyCount, node.props.canvasJson, canvasEditorRef]);

  const handleCommitHeight = useCallback(
    (h: number) => {
      if (h !== liveHeight) updateDocumentSize(liveWidth, h);
    },
    [liveHeight, liveWidth, updateDocumentSize],
  );
  return (
    <Box sx={{ position: 'relative', width: '100%', flexShrink: 0 }}>
      {toolbar}
      <Box
        ref={scaleRef}
        data-canvas-active
        sx={{
          position: 'relative',
          height: displayHeight * scale,
          width: '100%',
          outline: `2px solid ${INDIGO}`,
          outlineOffset: -2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width,
            height: liveHeight,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <CanvasKitEditor
            ref={canvasEditorRef}
            initialDocument={initialDoc}
            onDocumentChange={onCanvasDocumentChange ?? (() => {})}
            onSelectionChange={() => {}}
            disableAutoFit
            lockViewport
          />
        </Box>
        {!isHuggingArtboard ? (
          <CanvasHeightResizeHandle
            scale={scale}
            onPreview={setPreviewHeight}
            onCommit={handleCommitHeight}
          />
        ) : null}
      </Box>
    </Box>
  );
}

function PreviewCanvasSection({
  node,
  activeCanvasSectionId,
  canvasEditorRef,
  onCanvasDocumentChange,
  ...rest
}: {
  node: CanvasSectionNode;
} & PreviewNodeBaseProps) {
  const { handlers, outlineSx, selected } = useSelectableProps(node, rest.selectedNodeId, rest.onSelectNode, rest.hoveredNodeId, rest.onHoverNode);
  const hasStore = useHasEditorStore();
  const isActive = activeCanvasSectionId === node.id && hasStore;

  const toolbar = (selected || isActive) && rest.onSectionAction ? (
    <SectionToolbar
      nodeId={node.id}
      onAction={(a) => rest.onSectionAction!(node.id, a)}
      onDragStart={rest.onSectionDragStart}
    />
  ) : null;

  if (isActive) {
    return (
      <ActiveCanvasSection
        node={node}
        canvasEditorRef={canvasEditorRef}
        onCanvasDocumentChange={onCanvasDocumentChange}
        toolbar={toolbar}
      />
    );
  }

  // Empty canvasJson is fine — ReadOnlyCanvasSection defaults to an empty
  // SceneDocument so freshly-added canvas sections look the same as the
  // active editor's empty surface, not a gray svg placeholder.
  return (
    <ReadOnlyCanvasSection node={node} handlers={handlers} outlineSx={outlineSx} toolbar={toolbar} />
  );
}

interface CanvasObjectSvgProps {
  object: CanvasObject;
  selectedNodeId: string | null;
  onSelectNode: (id: string) => void;
}

function CanvasObjectSvg({ object, selectedNodeId, onSelectNode }: CanvasObjectSvgProps) {
  const selected = selectedNodeId === object.id;
  const { x, y, width, height, rotation, opacity } = object.props.transform;
  const transform = rotation ? `translate(${x},${y}) rotate(${rotation},${width / 2},${height / 2})` : `translate(${x},${y})`;
  const selectionProps = {
    'data-node-id': object.id,
    style: { cursor: 'pointer', opacity: opacity ?? 1 } as React.CSSProperties,
    onClick: (e: React.MouseEvent) => { e.stopPropagation(); onSelectNode(object.id); },
  };
  const selectionRect = selected ? (
    <rect x={0} y={0} width={width} height={height} fill="none" stroke="#6366F1" strokeWidth={2} strokeDasharray="4 2" pointerEvents="none" />
  ) : null;

  if (object.type === 'canvas-shape') {
    const fill = typeof object.style?.fill === 'string' ? object.style.fill : '#6366F1';
    const stroke = typeof object.style?.stroke === 'string' ? object.style.stroke : undefined;
    const shape = object.props.shape === 'ellipse'
      ? <ellipse cx={width / 2} cy={height / 2} rx={width / 2} ry={height / 2} fill={fill} stroke={stroke} strokeWidth={object.style?.strokeWidth} />
      : <rect width={width} height={height} rx={object.style?.borderRadius ?? 0} fill={fill} stroke={stroke} strokeWidth={object.style?.strokeWidth} />;
    return (
      <g transform={transform} {...selectionProps}>
        {shape}
        {selectionRect}
      </g>
    );
  }

  if (object.type === 'canvas-text') {
    const color = object.style?.color ?? '#111827';
    return (
      <g transform={transform} {...selectionProps}>
        <foreignObject width={width} height={height}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore - foreignObject requires html namespace on direct children */}
          <div
            style={{
              width, height, overflow: 'hidden',
              fontSize: object.style?.fontSize ?? 14,
              fontWeight: object.style?.fontWeight ?? 400,
              color,
              textAlign: object.style?.align ?? 'left',
              lineHeight: object.style?.lineHeight ?? 1.5,
              fontFamily: object.style?.fontFamily ?? 'inherit',
            }}
          >
            {object.props.content}
          </div>
        </foreignObject>
        {selectionRect}
      </g>
    );
  }

  if (object.type === 'canvas-image') {
    return (
      <g transform={transform} {...selectionProps}>
        {object.props.src
          ? <image href={typeof object.props.src === 'string' ? object.props.src : undefined} width={width} height={height} preserveAspectRatio={object.props.objectFit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice'} />
          : <rect width={width} height={height} fill="#E5E7EB" rx={2} />}
        {selectionRect}
      </g>
    );
  }

  if (object.type === 'canvas-path') {
    const fill = typeof object.style?.fill === 'string' ? object.style.fill : '#6366F1';
    const stroke = typeof object.style?.stroke === 'string' ? object.style.stroke : undefined;
    return (
      <g transform={transform} {...selectionProps}>
        <path d={object.props.d} fill={fill} stroke={stroke} strokeWidth={object.style?.strokeWidth} fillRule={object.style?.fillRule ?? 'nonzero'} />
        {selectionRect}
      </g>
    );
  }

  if (object.type === 'canvas-group') {
    return (
      <g transform={transform} {...selectionProps}>
        {object.children.map((child) => (
          <CanvasObjectSvg key={child.id} object={child} selectedNodeId={selectedNodeId} onSelectNode={onSelectNode} />
        ))}
        {selectionRect}
      </g>
    );
  }

  return null;
}

// ───────────────────────── helpers ─────────────────────────

function px(value?: number): string | undefined {
  return typeof value === 'number' ? `${value}px` : undefined;
}

// Mirror of shared-util `borderRadius()`. Scalar → "Npx"; object
// { tl, tr, br, bl } → "Apx Bpx Cpx Dpx" in CSS order. Missing corners → 0.
// Keeps preview parity with the compiled MJML output.
function cornerRadiusCss(
  value?: number | string | { tl?: number; tr?: number; br?: number; bl?: number },
): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return `${value}px`;
  // String form covers resolved $brand.borderRadius (e.g. "8px") and bare
  // numeric strings ("8") that come out of normalizeBorderRadius. Without
  // this branch the function would fall through to the object case and
  // read .tl/.tr/.br/.bl off a string — every corner resolves to 0px.
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
  }
  const c = (x: number | undefined) => `${x ?? 0}px`;
  return `${c(value.tl)} ${c(value.tr)} ${c(value.br)} ${c(value.bl)}`;
}

type ColorResolver = (value: string | undefined) => string | undefined;

// Identity resolver default; PreviewSection / PreviewColumn pass a real
// brand-token resolver from useResolveColor() so values like
// `$brand.color.primary` become concrete hex before they hit the DOM.
const identityColor: ColorResolver = (v) => v;

// Resolved background color of the nearest ancestor section/column. PreviewButton
// reads this to detect when its own bg matches what's behind it and falls back
// to an outlined treatment so it stays visible in the editor.
const SurroundingBackgroundContext = React.createContext<string | undefined>(undefined);

function normalizeHex(value: string | undefined): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  const v = value.trim().toLowerCase();
  if (!v.startsWith('#')) return v;
  if (v.length === 4) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  }
  return v;
}

function contrastTextOnHex(hex: string | undefined): string {
  const normalized = normalizeHex(hex);
  if (!normalized || !normalized.startsWith('#') || normalized.length !== 7) {
    return '#111827';
  }
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#111827' : '#FFFFFF';
}

// Resolves `$brand.color.X` to the live brand hex via DynamicPickerProvider.
// Returns identity for non-token strings. Without a brand context (no
// provider, e.g. in isolated tests) tokens pass through unchanged so storage
// values remain visible — the swatch already shows them stale-ish anyway.
function useResolveColor(): ColorResolver {
  const picker = useDynamicPicker();
  const brand = picker?.brand ?? null;
  return useCallback(
    (value) => {
      if (!value || typeof value !== 'string') return value;
      if (value.startsWith('$brand.color.')) {
        const usage = value.slice('$brand.color.'.length);
        return brand?.colorByUsage?.[usage] ?? value;
      }
      return value;
    },
    [brand],
  );
}

function borderStyleToCss(
  value: BorderStyle | string | undefined,
  resolveColor: ColorResolver = identityColor,
): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value || undefined;
  const color = resolveColor(value.color);
  if (!color || color.startsWith('$')) return undefined;
  const w = typeof value.width === 'number' ? value.width : 1;
  const s = value.style ?? 'solid';
  return `${w}px ${s} ${color}`;
}

// Mirror of shared-util `border()` — converts the unified `style.border`
// (scalar or per-side) plus the per-side `borderTop/Right/Bottom/Left` fields
// (Section/Text/Button) into React CSS props. Per-side fields override the
// scalar `border` shorthand, matching MJML's CSS order behavior.
function bordersToCss(
  style:
    | {
        border?: Border | string;
        borderTop?: BorderStyle | string;
        borderRight?: BorderStyle | string;
        borderBottom?: BorderStyle | string;
        borderLeft?: BorderStyle | string;
      }
    | undefined,
  resolveColor: ColorResolver = identityColor,
): React.CSSProperties {
  if (!style) return {};
  const out: React.CSSProperties = {};
  if (style.border) {
    if (typeof style.border === 'string') {
      out.border = style.border;
    } else {
      const b = style.border as BorderStyle | { top?: BorderStyle; right?: BorderStyle; bottom?: BorderStyle; left?: BorderStyle };
      if ('color' in b || 'width' in b || 'style' in b) {
        const css = borderStyleToCss(b as BorderStyle, resolveColor);
        if (css) out.border = css;
      } else {
        const sides = b as { top?: BorderStyle; right?: BorderStyle; bottom?: BorderStyle; left?: BorderStyle };
        const top = borderStyleToCss(sides.top, resolveColor);
        const right = borderStyleToCss(sides.right, resolveColor);
        const bottom = borderStyleToCss(sides.bottom, resolveColor);
        const left = borderStyleToCss(sides.left, resolveColor);
        if (top) out.borderTop = top;
        if (right) out.borderRight = right;
        if (bottom) out.borderBottom = bottom;
        if (left) out.borderLeft = left;
      }
    }
  }
  const top = borderStyleToCss(style.borderTop, resolveColor);
  const right = borderStyleToCss(style.borderRight, resolveColor);
  const bottom = borderStyleToCss(style.borderBottom, resolveColor);
  const left = borderStyleToCss(style.borderLeft, resolveColor);
  if (top) out.borderTop = top;
  if (right) out.borderRight = right;
  if (bottom) out.borderBottom = bottom;
  if (left) out.borderLeft = left;
  return out;
}

function spacingToCss(
  spacing?: number | { top?: number; right?: number; bottom?: number; left?: number }
): string | undefined {
  if (typeof spacing === 'number') return `${spacing}px`;
  if (!spacing) return undefined;
  return `${spacing.top ?? 0}px ${spacing.right ?? 0}px ${spacing.bottom ?? 0}px ${spacing.left ?? 0}px`;
}

function spacingToCssX(
  spacing?: number | { top?: number; right?: number; bottom?: number; left?: number }
): string | undefined {
  if (typeof spacing === 'number') return `${spacing}px`;
  if (!spacing) return undefined;
  return `${spacing.left ?? spacing.right ?? 0}px`;
}

function spacingToCssY(
  spacing?: number | { top?: number; right?: number; bottom?: number; left?: number }
): string | undefined {
  if (typeof spacing === 'number') return `${spacing}px`;
  if (!spacing) return undefined;
  return `${spacing.top ?? spacing.bottom ?? 0}px`;
}

function alignToJustify(align?: 'left' | 'center' | 'right') {
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return 'flex-start';
}

